import React from 'react';
import { Box, Paper, Typography, alpha, useTheme } from '@mui/material';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';

function formatSeconds(value) {
  const numeric = Number(value || 0);
  if (!numeric) return '–';
  if (numeric < 90) return `${numeric.toFixed(0)} Sek.`;
  if (numeric < 3600) return `${Math.round(numeric / 60)} Min.`;
  const hours = Math.floor(numeric / 3600);
  const minutes = Math.round((numeric % 3600) / 60);
  return minutes ? `${hours} Std. ${minutes} Min.` : `${hours} Std.`;
}

function WaitMetric({ label, value, meta }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 2,
        backgroundColor: alpha(theme.palette.common.white, 0.62),
        border: '1px solid',
        borderColor: alpha(theme.palette.primary.main, 0.08),
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.2 }}>
        {value}
      </Typography>
      {meta && (
        <Typography variant="caption" color="text.secondary">
          {meta}
        </Typography>
      )}
    </Box>
  );
}

export default function WaitStatsPanel({ waitStats }) {
  const theme = useTheme();
  const overall = waitStats?.overall || {};
  const days30 = waitStats?.days_30 || {};
  const gpuWait = waitStats?.gpu_wait_days_30 || {};

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1.5, sm: 2 },
        borderRadius: 3,
        backgroundColor: alpha(theme.palette.common.white, 0.78),
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}>
        <AccessTimeOutlinedIcon sx={{ color: 'primary.main', fontSize: 20 }} />
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.98rem' }}>
            Wartezeiten bis Start
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Typische und hohe Wartezeiten als Signal für GPU-Kapazität.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 1.25,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
        }}
      >
        <WaitMetric label="Median" value={formatSeconds(overall.median_wait_time)} />
        <WaitMetric label="P90" value={formatSeconds(overall.p90_wait_time)} />
        <WaitMetric label="P95" value={formatSeconds(overall.p95_wait_time)} />
        <WaitMetric label="Maximum" value={formatSeconds(overall.max_wait_time)} />
        <WaitMetric
          label="Über 15 Min."
          value={overall.wait_over_15_min_count || 0}
          meta="alle Jobs"
        />
        <WaitMetric
          label="Über 30 Min."
          value={overall.wait_over_30_min_count || 0}
          meta="alle Jobs"
        />
        <WaitMetric
          label="30 Tage P95"
          value={formatSeconds(days30.p95_wait_time)}
          meta={`${days30.count || 0} Jobs`}
        />
        <WaitMetric
          label="VRAM-Warten"
          value={formatSeconds(gpuWait.p95_wait_time)}
          meta={`${gpuWait.count || 0} Fälle in 30 Tagen`}
        />
      </Box>
    </Paper>
  );
}
